import { RefObject, useEffect } from "react"

type Options = {
  desktopMinWidth?: number
  stickyTop?: number
  mobileStickyTop?: number
  mobileMaxWidth?: number
}

export const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value))

/** * Helper to calculate scroll boundaries **/
const getScrollBounds = (section: HTMLElement, topOffset: number) => {
  const rect = section.getBoundingClientRect()
  const scrollY = window.scrollY
  const sectionOffsetTop = scrollY + rect.top

  const startPoint = sectionOffsetTop - topOffset
  const endPoint = startPoint + (section.offsetHeight - window.innerHeight)

  return { startPoint, endPoint, distance: endPoint - startPoint }
}

/** Scroll the window so `item` is centered in `scrollable` */
export function scrollWindowToNestedItem(
  section: HTMLElement,
  scrollable: HTMLElement,
  item: HTMLElement,
  options: Pick<
    Options,
    "stickyTop" | "mobileStickyTop" | "mobileMaxWidth"
  > = {}
): void {
  const {
    stickyTop = 106,
    mobileStickyTop = 96,
    mobileMaxWidth = 768,
  } = options

  const maxInnerScroll = scrollable.scrollHeight - scrollable.clientHeight
  if (maxInnerScroll <= 0)
    return item.scrollIntoView({ behavior: "smooth", block: "center" })

  const topOffset =
    window.innerWidth <= mobileMaxWidth ? mobileStickyTop : stickyTop
  const { startPoint, distance } = getScrollBounds(section, topOffset)

  if (distance <= 0)
    return item.scrollIntoView({ behavior: "smooth", block: "center" })

  // Calculate where the item is relative to the scrollable container's top
  const relativeTop = item.offsetTop - scrollable.offsetTop

  // Center the item within the scrollable viewport
  const centerOffset = (scrollable.clientHeight - item.clientHeight) / 2
  const desiredInnerScroll = clamp(
    relativeTop - centerOffset,
    0,
    maxInnerScroll
  )

  const progress = desiredInnerScroll / maxInnerScroll
  const targetPageY = startPoint + progress * distance

  window.scrollTo({ top: targetPageY, behavior: "smooth" })
}

export function useNestedScroll(
  sectionRef: RefObject<HTMLElement | null>,
  scrollableRef: RefObject<HTMLElement | null>,
  options: Options = {}
): void {
  const {
    desktopMinWidth = 0,
    stickyTop = 106,
    mobileStickyTop = 96,
    mobileMaxWidth = 768,
  } = options

  useEffect(() => {
    const section = sectionRef.current
    const scrollable = scrollableRef.current

    if (!section || !scrollable) return

    let ticking = false

    const syncScroll = () => {
      ticking = false

      // Early exit for desktop-only logic
      if (desktopMinWidth > 0 && window.innerWidth < desktopMinWidth) return

      const topOffset =
        window.innerWidth <= mobileMaxWidth ? mobileStickyTop : stickyTop
      const { startPoint, distance } = getScrollBounds(section, topOffset)
      const maxInnerScroll = scrollable.scrollHeight - scrollable.clientHeight

      if (distance <= 0 || maxInnerScroll <= 0) return

      const currentScrollY = window.scrollY
      const progress = clamp((currentScrollY - startPoint) / distance, 0, 1)

      // Using Math.floor/round prevents sub-pixel "shaking" in some browsers
      scrollable.scrollTop = Math.floor(progress * maxInnerScroll)
    }

    const onScroll = () => {
      if (!ticking) {
        ticking = true
        requestAnimationFrame(syncScroll)
      }
    }

    // Use ResizeObserver to catch dynamic content loading or window resizing
    const resizeObserver = new ResizeObserver(() => {
      syncScroll()
    })

    resizeObserver.observe(section)
    resizeObserver.observe(scrollable)
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", syncScroll)

    // Initial sync
    syncScroll()

    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", syncScroll)
      resizeObserver.disconnect()
    }
  }, [
    sectionRef,
    scrollableRef,
    desktopMinWidth,
    stickyTop,
    mobileStickyTop,
    mobileMaxWidth,
  ])
}
